import React from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	TextField,
	Paper,
	Chip,
	ChipPropsVariantOverrides,
	Box,
	Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { WorkBreakdowns } from '../../types/Project/WorkBreakdowns'
import { WBSWorkItemType } from '../../types/Project/WBSWorkItemType'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { WorkItemTemplate, ScaffoldWorkItem } from '../../types/Scaffold/Scaffold'
import { formatLocaleShortDate } from '../../services/CommonServices/DataFormatters'

interface WorkItemTemplateTableProps {
	isScaffoldBased: boolean
	selectedWBS: WorkBreakdowns | null
	workItemTemplates: WorkItemTemplate[]
	onAddTemplate: (wbsWorkItemType: WBSWorkItemType, parentId?: string) => void
	onRemoveTemplate: (id: string) => void
	onUpdateTemplate: (id: string, field: keyof WorkItemTemplate, value: any) => void
	existingHierarchy?: ScaffoldWorkItem[]
}

export default function WorkItemTemplateTable({
	isScaffoldBased,
	selectedWBS,
	workItemTemplates,
	onAddTemplate,
	onRemoveTemplate,
	onUpdateTemplate,
	existingHierarchy = [],
}: WorkItemTemplateTableProps) {
	// Check if a template represents an existing work item from the hierarchy
	const isExistingWorkItem = (template: WorkItemTemplate): boolean => {
		return existingHierarchy.some(
			(item) => item.workItemTypeId === template.wbsWorkItemType.id
		)
	}

	const getLayerVariant = (wbsWorkItemType: WBSWorkItemType): keyof ChipPropsVariantOverrides => {
		const layerIndex = Math.min(Math.max(wbsWorkItemType.index || 1, 1), 5)
		return `Layer${layerIndex}${selectedWBS?.isOutOfScope ? 'OutOfScope' : 'WorkItem'}` as keyof ChipPropsVariantOverrides
	}
	const flattenForDisplay = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
		const result: WorkItemTemplate[] = []

		const flatten = (items: WorkItemTemplate[]) => {
			const sortedItems = [...items].sort(
				(a, b) => a.wbsWorkItemType.index - b.wbsWorkItemType.index,
			)

			sortedItems.forEach((item) => {
				result.push(item)
				if (item.children && item.children.length > 0) {
					flatten(item.children)
				}
			})
		}

		flatten(templates)
		return result
	}

	const displayTemplates = flattenForDisplay(workItemTemplates)

	if (!selectedWBS || workItemTemplates.length === 0) {
		return null
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TableContainer component={Paper} sx={{ maxHeight: 400 }}>
				<Table stickyHeader size='small'>
					<TableHead>
						<TableRow>
							<TableCell>Actions</TableCell>
							<TableCell align='left'>Type</TableCell>
							<TableCell align='center'>WBS</TableCell>
							<TableCell sx={{ minWidth: 200 }}>Name</TableCell>
							<TableCell align='center' sx={{ minWidth: 150 }}>Release Date</TableCell>
							<TableCell align='center' sx={{ minWidth: 150 }}>NOC Date</TableCell>
							<TableCell align='center'>Qty Budget</TableCell>
							<TableCell align='center'>Unit</TableCell>
							<TableCell align='center'>Budget Hours</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{displayTemplates.map((template) => {
							const isExisting = isExistingWorkItem(template)
							return (
								<TableRow key={template.id}>
									<TableCell>
										<IconButton
											size='small'
											color='primary'
											onClick={() => onAddTemplate(template.wbsWorkItemType, template.parentId)}
											title='Add new work item of this type'
										>
											<AddIcon />
										</IconButton>
										{displayTemplates.length > 1 && !isExisting && (
											<IconButton
												size='small'
												color='error'
												onClick={() => onRemoveTemplate(template.id)}
												title='Remove this template'
											>
												<DeleteIcon />
											</IconButton>
										)}
									</TableCell>
									<TableCell align='left'>
										<Box sx={{ pl: (template.level - 1) * 2 }}>
											<Chip
												label={template.wbsWorkItemType.name}
												variant={getLayerVariant(template.wbsWorkItemType)}
											/>
										</Box>
									</TableCell>
									<TableCell align='center'>{selectedWBS.name}</TableCell>
									<TableCell sx={{ minWidth: 300 }}>
										{!isExisting ? (<TextField
											fullWidth
											variant='outlined'
											size='small'
											value={template.name}
											onChange={(e) => onUpdateTemplate(template.id, 'name', e.target.value)}
											placeholder='Work item name'
										/>
										) : (
											<Typography variant='body2'>{template.name}</Typography>
										)}
									</TableCell>
									<TableCell align='center' sx={{ minWidth: 170 }}>
										{isScaffoldBased || isExisting ? (
											template.releaseDate || "n/a"
										) : (
											<DatePicker
												value={template.releaseDate ? dayjs(template.releaseDate) : null}
												onChange={(newValue) => {
													const dateString = newValue
														? formatLocaleShortDate(newValue.toDate())
														: ''
													onUpdateTemplate(template.id, 'releaseDate', dateString)
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														variant='outlined'
														size='small'
														fullWidth
													/>
												)}
											/>
										)}
									</TableCell>
									<TableCell align='center' sx={{ minWidth: 170 }}>
										{isScaffoldBased || isExisting ? (
											template.nocDate || "n/a"
										) : (
											<DatePicker
												value={template.nocDate ? dayjs(template.nocDate) : null}
												onChange={(newValue) => {
													const dateString = newValue
														? formatLocaleShortDate(newValue.toDate())
														: ''
													onUpdateTemplate(template.id, 'nocDate', dateString)
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														variant='outlined'
														size='small'
														fullWidth
													/>
												)}
											/>
										)}
									</TableCell>
									<TableCell align='center'>
										{template.children && template.children.length > 0 ? (
											template.quantityBudgeted
										) : (
											<TextField
												type='number'
												variant='outlined'
												size='small'
												value={template.quantityBudgeted}
												onChange={(e) =>
													onUpdateTemplate(
														template.id,
														'quantityBudgeted',
														parseFloat(e.target.value) || 0,
													)
												}
												inputProps={{ min: 0, step: 0.01 }}
												disabled={isExisting}
											/>
										)}
									</TableCell>
									<TableCell align='center'>{template.unitOfMeasure}</TableCell>
									<TableCell align='center'>
										{template.children && template.children.length > 0 ? (
											template.budgetedHours
										) : (
											<TextField
												type='number'
												variant='outlined'
												size='small'
												value={template.budgetedHours}
												onChange={(e) =>
													onUpdateTemplate(
														template.id,
														'budgetedHours',
														parseFloat(e.target.value) || 0,
													)
												}
												inputProps={{ min: 0, step: 0.1 }}
												disabled={isExisting}
											/>
										)}
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</LocalizationProvider>
	)
}
